import { useCallback, useEffect, useState } from "react";
import api, { euro, fmtDate, todayISO, can } from "../lib/api";
import { useQuick } from "../components/Layout";
import { Plus, Trash2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Payments() {
  const { openModal } = useQuick();
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    const { data } = await api.get("/payments", { params });
    setPayments(data);
  }, [from, to]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("ll:data-changed", h);
    return () => window.removeEventListener("ll:data-changed", h);
  }, [load]);

  const del = async (p) => {
    if (!window.confirm("Eliminare questo pagamento?")) return;
    await api.delete(`/payments/${p.id}`);
    toast.success("Pagamento eliminato");
    load();
  };

  const total = payments.reduce((s, p) => s + (p.amount || 0), 0);

  const exportCSV = () => {
    const rows = [["Data", "Paziente", "Causale", "Metodo", "Importo", "Note"]];
    payments.forEach((p) => rows.push([p.date, p.patient_name, p.causale, p.method, p.amount, p.notes || ""]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = `pagamenti_${todayISO()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="payments-page">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-heading text-2xl font-bold">Pagamenti</h1>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <input data-testid="payments-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white" />
          <input data-testid="payments-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white" />
          <button data-testid="payments-export" onClick={exportCSV} className="p-2.5 rounded-xl bg-white border border-slate-200" title="Esporta Excel"><FileSpreadsheet size={15} /></button>
          <button data-testid="payments-new" onClick={() => openModal("payment")} className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2.5 rounded-full text-sm font-medium"><Plus size={15} /> Pagamento</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-center justify-between" data-testid="payments-total">
        <p className="text-sm text-slate-500">{payments.length} movimenti nel periodo</p>
        <p className="font-heading text-2xl font-bold text-emerald-600">{euro(total)}</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
              <th className="py-3 px-4">Data</th><th className="px-3">Paziente</th><th className="px-3">Causale</th><th className="px-3">Metodo</th><th className="px-3">Importo</th><th className="px-3"></th>
            </tr></thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} data-testid={`payment-row-${p.id}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="py-3 px-4 whitespace-nowrap">{fmtDate(p.date)}</td>
                  <td className="px-3 font-medium">{p.patient_name}</td>
                  <td className="px-3 text-slate-600">{p.causale || "—"}</td>
                  <td className="px-3 capitalize text-slate-600">{p.method}</td>
                  <td className="px-3 font-semibold text-emerald-600">{euro(p.amount)}</td>
                  <td className="px-3">{can(user, "pagamenti", "delete") && <button data-testid={`payment-delete-${p.id}`} onClick={() => del(p)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>}</td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-slate-400" data-testid="payments-empty">Nessun pagamento nel periodo</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
